import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Building2, CalendarDays, ImageUp, Loader2, MapPin, QrCode, Ticket, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, fmtDate } from "@/lib/api";
import { Navbar } from "@/components/landing/Hero";
import { CoverImage } from "@/components/CoverImage";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400";
const btnPrimary = "inline-flex items-center justify-center gap-2 bg-indigo-600 text-white font-bold rounded-full px-6 py-3 shadow-md hover:bg-indigo-700 active:scale-95 transition disabled:opacity-50";
const selectTriggerCls = "w-full rounded-xl border border-slate-200 px-4 py-3 h-auto text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", ticket_type: "" });
  const [method, setMethod] = useState("");
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(`/public/ticketing/events/${id}`).then((r) => {
      setEvent(r.data);
      if (r.data.ticket_types?.length) setForm((f) => ({ ...f, ticket_type: r.data.ticket_types[0].name }));
    }).catch(() => setEvent(false));
    api.get("/public/ticketing/payment-config").then((r) => {
      setCfg(r.data);
      const opts = [];
      if (r.data.gateways?.includes("midtrans")) opts.push("qris", "va");
      if (r.data.bank_account || r.data.qris_image_url) opts.push("manual");
      setMethod(opts[0] || "");
    }).catch(() => setCfg({ gateways: [] }));
    window.scrollTo(0, 0);
  }, [id]);

  const uploadProof = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/public/ticketing/upload-proof", fd);
      setProof({ path: data.path, name: file.name, preview: URL.createObjectURL(file) });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (price > 0 && method === "manual" && !proof) return toast.error("Unggah bukti pembayaran terlebih dahulu");
    setBusy(true);
    try {
      const returnUrl = `${window.location.origin}/event/${id}/tiket`;
      const gateway = method === "manual" ? "manual" : method ? "midtrans" : undefined;
      const { data } = await api.post(`/public/ticketing/events/${id}/register`, {
        ...form, gateway: price > 0 ? gateway : undefined, proof_path: method === "manual" ? proof?.path : undefined, return_url: returnUrl,
      });
      if (data.payment_required && data.payment_url) {
        window.location.href = data.payment_url;
      } else {
        navigate(`/event/${id}/tiket?code=${data.ticket_code}`);
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (event === false) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="event-detail-notfound">
        <Navbar forceGlass />
        <div className="pt-40 pb-20 text-center px-6">
          <p className="font-black text-xl text-slate-900">Event tidak ditemukan</p>
          <Link to="/event" className="inline-block mt-5 font-bold text-indigo-600">← Kembali ke daftar event</Link>
        </div>
      </div>
    );
  }
  if (!event || !cfg) {
    return <div className="min-h-screen bg-slate-50 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  const full = event.quota ? event.registrations >= event.quota : false;
  const selectedTicket = event.ticket_types?.find((t) => t.name === form.ticket_type);
  const price = selectedTicket ? selectedTicket.price : (event.price || 0);
  const methodOptions = [];
  if (cfg.gateways?.includes("midtrans")) methodOptions.push({ key: "qris", label: "QRIS" }, { key: "va", label: "Virtual Account" });
  if (cfg.bank_account || cfg.qris_image_url) methodOptions.push({ key: "manual", label: "Transfer Manual" });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="event-detail-page">
      <Navbar forceGlass />
      <section className="pt-28 pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          <Link to="/event" data-testid="event-detail-back" className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-slate-900 transition"><ArrowLeft className="w-4 h-4" />Semua Event</Link>
          <div className="mt-5 grid lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3">
              <CoverImage src={fileUrl(event.image_url)} alt={event.title} className="aspect-video rounded-3xl" />
              {event.category && <span className="inline-block mt-5 text-[10px] font-bold uppercase tracking-wider text-indigo-600 bg-indigo-50 rounded-full px-2.5 py-1">{event.category}</span>}
              <h1 className="mt-3 text-3xl md:text-4xl font-black text-slate-900 tracking-tight" data-testid="event-detail-title">{event.title}</h1>
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-500 font-semibold">
                <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{fmtDate(event.start_date, { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · {event.time}</span>
                {event.location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{event.location}</span>}
                <span className="flex items-center gap-1.5"><Users className="w-4 h-4" />{event.registrations} terdaftar{event.quota ? ` / kuota ${event.quota}` : ""}</span>
              </div>
              {event.description && <p className="mt-6 text-slate-600 leading-relaxed whitespace-pre-line">{event.description}</p>}
            </div>

            <div className="lg:col-span-2">
              <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 sticky top-28" data-testid="event-register-card">
                <p className="font-extrabold text-slate-900 flex items-center gap-2"><Ticket className="w-5 h-5 text-indigo-600" />Daftar Tiket</p>
                <p className="mt-1 text-xs text-slate-500">Terbuka untuk umum — tidak perlu akun jemaat/NIG.</p>
                {full ? (
                  <p className="mt-5 text-sm font-bold text-red-600 bg-red-50 rounded-xl p-4" data-testid="event-register-full">Mohon maaf, kuota event ini sudah penuh.</p>
                ) : (
                  <form onSubmit={submit} className="mt-5 space-y-3">
                    <input required className={inputCls} placeholder="Nama lengkap" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="event-register-name" />
                    <input required type="email" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="event-register-email" />
                    <input required className={inputCls} placeholder="No. WhatsApp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="event-register-phone" />
                    {event.ticket_types?.length > 0 && (
                      <Select value={form.ticket_type} onValueChange={(v) => setForm({ ...form, ticket_type: v })}>
                        <SelectTrigger className={selectTriggerCls} data-testid="event-register-ticket-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {event.ticket_types.map((t) => <SelectItem key={t.name} value={t.name}>{t.name} — {t.price ? `Rp ${t.price.toLocaleString("id-ID")}` : "Gratis"}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    {price > 0 && methodOptions.length > 0 && (
                      <Select value={method} onValueChange={setMethod}>
                        <SelectTrigger className={selectTriggerCls} data-testid="event-register-gateway"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {methodOptions.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    {price > 0 && methodOptions.length === 0 && (
                      <p className="text-xs font-bold text-amber-700 bg-amber-50 rounded-xl p-3" data-testid="event-pay-unavailable">Pembayaran untuk event ini belum diaktifkan admin. Silakan hubungi panitia.</p>
                    )}
                    {price > 0 && method === "manual" && (
                      <div className="space-y-3 pt-1" data-testid="event-manual-panel">
                        {cfg.bank_account && (
                          <div className="rounded-2xl bg-slate-900 text-white p-4">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-white/50">Rekening {cfg.bank_name}</p>
                            <p className="text-lg font-black tabular-nums tracking-wide" data-testid="event-bank-account">{cfg.bank_account}</p>
                            <p className="text-xs text-white/60">a.n. {cfg.bank_holder}</p>
                          </div>
                        )}
                        {cfg.qris_image_url && (
                          <div className="text-center">
                            <img src={fileUrl(cfg.qris_image_url)} alt="QRIS" className="w-40 mx-auto rounded-2xl border border-slate-100" data-testid="event-qris-image" />
                            <p className="mt-1 text-[11px] text-slate-400 flex items-center justify-center gap-1"><QrCode className="w-3 h-3" />Scan untuk bayar via QRIS</p>
                          </div>
                        )}
                        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadProof(e.target.files?.[0])} data-testid="event-proof-input" />
                        <button type="button" onClick={() => fileRef.current?.click()} data-testid="event-proof-button" className="w-full border-2 border-dashed border-slate-200 rounded-2xl p-4 flex items-center gap-3 hover:border-indigo-500 hover:bg-indigo-50/40 transition">
                          {proof?.preview ? <img src={proof.preview} alt="" className="w-11 h-11 rounded-xl object-cover" /> : <span className="w-11 h-11 rounded-xl bg-indigo-100 text-indigo-700 grid place-items-center shrink-0"><ImageUp className="w-5 h-5" /></span>}
                          <span className="text-left min-w-0"><span className="block font-bold text-slate-900 text-sm">{proof ? "Bukti terunggah" : "Unggah bukti pembayaran"}</span><span className="block text-xs text-slate-500 truncate">{proof ? proof.name : "JPG, PNG, atau WEBP · maks 8MB"}</span></span>
                        </button>
                        <p className="text-[11px] text-slate-400 flex items-center gap-1.5"><Building2 className="w-3 h-3" />Tiket aktif setelah pembayaran diverifikasi panitia.</p>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-sm text-slate-500 font-semibold">Total</span>
                      <span className="text-xl font-black text-slate-900">{price ? `Rp ${price.toLocaleString("id-ID")}` : "Gratis"}</span>
                    </div>
                    <button disabled={busy || (price > 0 && methodOptions.length === 0)} className={`${btnPrimary} w-full`} data-testid="event-register-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : price ? (method === "manual" ? "Kirim Pendaftaran" : "Lanjut Bayar") : "Daftar Sekarang"}</button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
